import { useEffect, useState } from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Icon,
  Modal,
  Radio,
  Select,
  Text,
  TooltipWrapper
} from "@theorgchartdev/storybook";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import * as StateLib from "@/Utils/StateLib";
import { enumContainerType, enumHierarchyTypes } from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import { Globals } from "@coreapp/CoreApp/Globals";

const COLUMNS_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8].map(n => ({
  label: String(n),
  value: n
}));

const LayoutTypeModal = ({ show, onClose }) => {
  const { setWsMgr } = useWSMgrContext();
  const wsMgr = StateLib.GetWSMgr();

  const [containerType, setContainerType] = useState(enumContainerType.nonmrb);
  const [numCols, setNumCols] = useState(2);

  useEffect(() => {
    if (!show) return;
    const layout = wsMgr?.GetBLELayout();
    if (!layout) return;
    setContainerType(layout.GetContainerType() ?? enumContainerType.nonmrb);
    setNumCols(layout.GetContainerNumCols() ?? 2);
  }, [show]);

  const isGroupBox = containerType === enumContainerType.standardmrb;

  const handleSave = () => {
    const layout = wsMgr?.GetBLELayout();
    if (!layout) return;
    layout.SetContainerType(containerType);
    layout.SetHierarchyType(
      isGroupBox ? enumHierarchyTypes.grouping : enumHierarchyTypes.employees
    );
    if (isGroupBox) {
      layout.SetContainerNumCols(numCols);
    }
    // Stage as an edit draft so the toolbar Save picks up these mutations.
    wsMgr.OnLayoutMutated?.();
    wsMgr.UpdateBoxSizingMode(true);
    StateLib.ReRenderCanvas();
    setWsMgr({ state: Globals._wsMgr });
    onClose();
  };

  const columnOptions = COLUMNS_OPTIONS.map(opt => ({
    ...opt,
    clickHandler: () => setNumCols(opt.value)
  }));

  return (
    <CentralizedModalContainer show={show}>
      <Modal id="layout-type-modal" show={show} onClose={onClose} width="400px">
        <DialogTitle divider={true} onClose={onClose}>
          {appstr("Layout type")}
        </DialogTitle>
        <DialogContent>
          <Flex direction="column" gap="16px" margin="8px 0 0 0">
            <Flex direction="column" gap="16px">
              <Text size="small" modifier="bold">
                {appstr("Layout type")}
              </Text>
              <Flex direction="row" gap="32px">
                <Flex
                  direction="row"
                  alignItems="center"
                  gap="8px"
                  className="layout-type-modal-radio-button"
                  dataTestId="box-layout-editor-layout-type-standard-radio"
                >
                  <Radio
                    id="layout-type-standard-radio"
                    label={appstr("Employee box")}
                    size="small"
                    checked={containerType === enumContainerType.nonmrb}
                    onChange={() => setContainerType(enumContainerType.nonmrb)}
                  />
                  <TooltipWrapper
                    tooltipProps={{
                      message: appstr("This box layout type is applied to employee boxes."),
                      popoverProps: { referenceProps: { className: "layout-type-modal-tooltip" } }
                    }}
                  >
                    <Icon name="circle-info" color="info" size="small" />
                  </TooltipWrapper>
                </Flex>
                <Flex
                  direction="row"
                  alignItems="center"
                  gap="8px"
                  className="layout-type-modal-radio-button"
                  dataTestId="box-layout-editor-layout-type-group-radio"
                >
                  <Radio
                    id="layout-type-group-radio"
                    label={appstr("Group box")}
                    size="small"
                    checked={containerType === enumContainerType.standardmrb}
                    onChange={() => setContainerType(enumContainerType.standardmrb)}
                  />
                  <TooltipWrapper
                    tooltipProps={{
                      message: appstr(
                        "This box layout type is applied to the grouped box that groups the employee records."
                      ),
                      popoverProps: { referenceProps: { className: "layout-type-modal-tooltip" } }
                    }}
                  >
                    <Icon name="circle-info" color="info" size="small" />
                  </TooltipWrapper>
                </Flex>
              </Flex>
            </Flex>
            {isGroupBox && (
              <Flex direction="column" gap="8px">
                <Text size="small" modifier="bold">
                  {appstr("Display settings")}
                </Text>
                <Flex direction="row" alignItems="center" gap="8px">
                  <Select
                    dataTestId="box-layout-editor-layout-type-columns-select"
                    size="small"
                    value={numCols}
                    options={columnOptions}
                    inputOnly={true}
                    aria-label={appstr("columns used in the group box")}
                  />
                  <Text size="small">{appstr("columns used in the group box")}</Text>
                </Flex>
              </Flex>
            )}
          </Flex>
        </DialogContent>
        <DialogActions divider={true} size="medium">
          <DialogActions.Secondary
            dataTestId="box-layout-editor-layout-type-cancel-btn"
            onClick={onClose}
          >
            {appstr("Cancel")}
          </DialogActions.Secondary>
          <DialogActions.Primary onClick={handleSave} dataTestId="layout-type-modal-save-btn">
            {appstr("Save")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

export default LayoutTypeModal;
